import { ServerTimeObservation } from './corrected-clock'

/** An offset learned in an earlier session, and when it was learned. */
export interface PersistedOffset {
    /** What was added to the local clock to get the server's. */
    offsetMs: number;
    /** The local clock when that was written down. */
    savedAtClientMs: number;
}

/**
 * How long a remembered offset stays usable. It rests entirely on nothing
 * having stepped the local clock since it was written, and that assumption
 * decays with no way to check it.
 */
export const PERSISTED_OFFSET_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Deliberately coarser than every live source, so a page that has one prefers
 * it. This is a guess carried across sessions: the clock may have drifted or
 * been stepped by an unknown amount since.
 */
export const PERSISTED_OFFSET_UNCERTAINTY_MS = 60 * 1000;

const STORAGE_KEY = '__gp_server_time_offset';

export function observationFromPersistedOffset(
    persisted: PersistedOffset | null,
    localNowMs: number,
): ServerTimeObservation | null {

    if (!persisted || typeof persisted.offsetMs !== 'number' || !isFinite(persisted.offsetMs)) {
        return null;
    }

    const ageMs = localNowMs - persisted.savedAtClientMs;

    // A negative age means the local clock moved backwards since the record was
    // written, which falsifies the one thing the record depends on.
    if (ageMs < 0 || ageMs > PERSISTED_OFFSET_MAX_AGE_MS) {
        return null;
    }

    return {
        serverUtcMs: localNowMs + persisted.offsetMs,
        clientUtcMs: localNowMs,
        uncertaintyMs: PERSISTED_OFFSET_UNCERTAINTY_MS,
    };
}

/** Reads the remembered offset. Defensive: storage is absent in workers and can throw under privacy settings. */
export function readPersistedOffset(): PersistedOffset | null {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (typeof parsed?.offsetMs !== 'number' || typeof parsed?.savedAtClientMs !== 'number') {
            return null;
        }

        return { offsetMs: parsed.offsetMs, savedAtClientMs: parsed.savedAtClientMs };
    } catch (e) {
        return null;
    }
}

/** Writes an offset down for the next page load. Defensive for the same reasons. */
export function rememberOffset(observation: ServerTimeObservation): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
            offsetMs: observation.serverUtcMs - observation.clientUtcMs,
            savedAtClientMs: observation.clientUtcMs,
        }));
    } catch (e) {
        // Storage full, disabled, or absent. The next load falls back to
        // whatever live source reaches it, which is the normal path anyway.
    }
}
